/**
 * Client-side, read-only decoder for the opaque `context` token — never used to
 * verify or trust it, only to make an admin's debug view readable. Wire format per
 * the backend (`dialogcodec.Codec`): `v1.` + base64url(JSON payload) + `.` +
 * base64url(HMAC-SHA256 signature), no padding. The payload is signed, not
 * encrypted, so it's plain JSON once base64url-decoded; the signature segment is a
 * raw digest, not itself decodable.
 */
export interface DecodedContextToken {
  version: string;
  /** Pretty-printed JSON of the decoded payload. */
  payloadRaw: string;
  /** Raw base64url signature segment — not decodable, shown as-is. */
  signature: string;
}

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Returns `null` for anything that isn't a well-formed `v1.<payload>.<sig>` token —
 *  e.g. mock/dev tokens, which don't follow this format at all. */
export function decodeContextToken(token: string): DecodedContextToken | null {
  const parts = token.split('.');
  if (parts.length !== 3 || parts[0] !== 'v1') return null;

  const [version, payloadB64, signature] = parts;
  try {
    const payload = JSON.parse(base64UrlDecode(payloadB64));
    return {version, payloadRaw: JSON.stringify(payload, null, 2), signature};
  } catch {
    return null;
  }
}
